/**
 * The banner over every screen while a baseline is the current view (issue #97,
 * phase D, read-only layer three).
 *
 * A baseline is a fixed copy of a tag, so what is on the screen is not the live
 * model and cannot be changed. Saying so on EVERY screen, with the way back, is the
 * point: the server refuses the writes (layer one), but a person looking at a
 * requirement from last quarter must not mistake it for today's.
 *
 * What it tells them beyond that, from the baseline's status: which BEST cut it and
 * which is showing it when they differ (a baseline cut before a model migration
 * is read by today's code), which Echelon library the view is built with, and the
 * server's own note on that. Everything from the server is plain text, and none
 * of it is needed for the banner to say what it must: if the status cannot be read
 * the banner still says "read only" and still offers the way back.
 *
 * Draws nothing when no baseline is being viewed, and so costs nothing then.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { api } from '../../api'
import type { BaselineInfo } from '../../schema'
import { usePrincipal } from '../../state/AppData'
import { viewedBaseline } from '../../state/baselineRef'
import { useProject } from '../../state/Project'
import { MONOSPACE } from '../../theme'
import { useBaselineView } from './useBaselineView'

/** How the library line reads for each state the server reports. */
const LIBRARY_LABEL: Record<BaselineInfo['library'], string> = {
  pinned: 'Echelon library: pinned to the version this baseline was cut with',
  'archive-missing':
    'Echelon library: the archived copy is missing, shown with the current library',
  unpinned:
    'Echelon library: cut before the library was recorded, shown with the current library',
}

export default function BaselineBanner() {
  const principal = usePrincipal()
  const { current, branch } = useProject()
  const { back, busy } = useBaselineView()
  const tag = viewedBaseline(principal.baseline, branch)
  const slug = current?.slug ?? ''

  // Keyed by tag: the info read for one baseline is never shown for another.
  const [read, setRead] = useState<{ tag: string; info: BaselineInfo | null } | null>(null)

  useEffect(() => {
    if (!tag || !slug) return undefined
    let cancelled = false
    api
      .baselineStatus(slug, tag)
      .then((status) => {
        if (!cancelled) setRead({ tag, info: status.info })
      })
      // Decoration: the banner stands without it.
      .catch(() => {
        if (!cancelled) setRead({ tag, info: null })
      })
    return () => {
      cancelled = true
    }
  }, [slug, tag])

  if (!tag) return null

  const info = read?.tag === tag ? read.info : null
  const versionsDiffer = Boolean(
    info && info.cut_with && info.shown_with && info.cut_with !== info.shown_with,
  )
  const defaultBranch = current?.default_branch || 'main'

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      data-testid="baseline-banner"
      action={
        <Button color="inherit" size="small" onClick={() => void back()} disabled={busy}>
          {`Back to ${defaultBranch}`}
        </Button>
      }
    >
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Viewing baseline{' '}
          <Box component="span" sx={{ fontFamily: MONOSPACE }}>
            {tag}
          </Box>{' '}
          — read only.
        </Typography>
        {versionsDiffer && info ? (
          <Typography variant="body2">
            {`Cut under BEST ${info.cut_with}, shown with BEST ${info.shown_with}.`}
          </Typography>
        ) : null}
        {info ? (
          <Typography variant="body2">{LIBRARY_LABEL[info.library] ?? ''}</Typography>
        ) : null}
        {info?.library_note ? (
          <Typography variant="body2" color="text.secondary">
            {info.library_note}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  )
}
